import { ValidationHelper } from '@amzn/awscat-pattern-library-service-model';

/**
 * Node types the inline-edit feature accepts from agent output. Must stay in
 * sync with the editor schema (TiptapEditingExtensionConfig). Anything outside
 * this set is rejected rather than dropped, so untrusted output cannot smuggle
 * in node types the reviewer never expects to render.
 */
const ALLOWED_NODE_TYPES: ReadonlySet<string> = new Set([
  'doc',
  'document',
  'paragraph',
  'text',
  'heading',
  'bulletList',
  'orderedList',
  'listItem',
  'blockquote',
  'horizontalRule',
  'hardBreak',
  'codeBlock',
  'table',
  'tableRow',
  'tableHeader',
  'tableCell',
  'image',
  'mention',
  'apgl-entity',
  'youtube',
]);

/**
 * Mark types the inline-edit feature accepts from agent output — plain text
 * formatting plus links. Comment and proposal marks are intentionally excluded:
 * a suggested content edit never carries them.
 */
const ALLOWED_MARK_TYPES: ReadonlySet<string> = new Set([
  'bold',
  'italic',
  'underline',
  'strike',
  'code',
  'link',
]);

/** Forced rel on every agent-supplied link, matching the extension default a null attr can strip. */
const SAFE_LINK_REL = 'noopener noreferrer nofollow';

/** Forced target on every agent-supplied link. */
const SAFE_LINK_TARGET = '_blank';

/**
 * URL-bearing attribute keys per allowed node type. These are the only node
 * attributes that reach a URL sink on render (image `<img src>`, youtube iframe
 * embed), so they must satisfy the same content-link policy as link hrefs. The
 * other non-text allowed nodes carry no URL attribute — `mention` renders a
 * `<span class="mention">` from id/label and `apgl-entity` a `<span data-id>`
 * from id alone — so neither needs URL validation.
 */
const URL_NODE_ATTRS: ReadonlyMap<string, readonly string[]> = new Map([
  ['image', ['src']],
  ['youtube', ['src']],
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function describeType(value: unknown): string {
  return isRecord(value) && typeof value.type === 'string' ? value.type : typeof value;
}

function normalizeContentUrl(rawUrl: unknown, label: string): string {
  // Require a string — a null or missing URL is how the extension default gets bypassed.
  if (typeof rawUrl !== 'string') {
    throw new InlineEditSanitizationError(`${label} is not a string`);
  }

  // Upgrade insecure http to https so agent URLs share the author-side policy.
  const trimmed = rawUrl.trim();
  const upgraded = trimmed.toLowerCase().startsWith('http://')
    ? trimmed.replace(/^http:\/\//i, 'https://')
    : trimmed;

  // Enforce the shared content-link policy (valid URL + https, or in-document anchor).
  if (!ValidationHelper.isValidContentLink(upgraded)) {
    throw new InlineEditSanitizationError(`${label} failed validation: ${upgraded}`);
  }
  return upgraded;
}

function sanitizeNodeAttrs(type: string, attrs: Record<string, unknown>): Record<string, unknown> {
  const urlKeys = URL_NODE_ATTRS.get(type);
  if (!urlKeys) return attrs;

  // Normalize each URL-bearing attribute; a present-but-invalid URL rejects the payload.
  // An absent or null URL attribute is left as-is (the node simply has no URL sink).
  const sanitized: Record<string, unknown> = { ...attrs };
  for (const key of urlKeys) {
    const value = attrs[key];
    if (value === undefined || value === null) continue;
    sanitized[key] = normalizeContentUrl(value, `${type} node ${key}`);
  }
  return sanitized;
}

function sanitizeMark(mark: unknown): SanitizedMark {
  // Reject marks that are malformed or outside the allowlist.
  if (!isRecord(mark) || typeof mark.type !== 'string' || !ALLOWED_MARK_TYPES.has(mark.type)) {
    throw new InlineEditSanitizationError(`Disallowed inline-edit mark: ${describeType(mark)}`);
  }

  // Formatting marks carry no attributes the feature needs, so drop them entirely.
  if (mark.type !== 'link') {
    return { type: mark.type };
  }

  // Link marks: keep only a validated href, dropping every other agent-supplied attr.
  const attrs = isRecord(mark.attrs) ? mark.attrs : {};
  const href = normalizeContentUrl(attrs.href, 'Link mark href');

  // In-document anchors and relative paths navigate within the app, so leave them
  // bare — forcing _blank would wrongly open them in a new tab. Only external https
  // links get forced rel/target, restoring the extension default a null attr can strip
  // so the new tab cannot reverse-tabnab.
  if (!href.toLowerCase().startsWith('https://')) {
    return { type: 'link', attrs: { href } };
  }
  return {
    type: 'link',
    attrs: { href, target: SAFE_LINK_TARGET, rel: SAFE_LINK_REL },
  };
}

function sanitizeNode(node: unknown): SanitizedNode {
  // Reject nodes that are malformed or outside the allowlist.
  if (!isRecord(node) || typeof node.type !== 'string' || !ALLOWED_NODE_TYPES.has(node.type)) {
    throw new InlineEditSanitizationError(`Disallowed inline-edit node: ${describeType(node)}`);
  }

  // Sanitize marks and child content; a single violation rejects the whole payload.
  const marks = Array.isArray(node.marks) ? node.marks.map(sanitizeMark) : undefined;
  const content = Array.isArray(node.content) ? node.content.map(sanitizeNode) : undefined;

  // Preserve node attrs and text verbatim, but validate URL-bearing attributes
  // (image/youtube src) first. ProseMirror drops unknown attr keys on create.
  const sanitized: {
    type: string;
    attrs?: Record<string, unknown>;
    text?: string;
    marks?: readonly SanitizedMark[];
    content?: readonly SanitizedNode[];
  } = { type: node.type };
  if (isRecord(node.attrs)) sanitized.attrs = sanitizeNodeAttrs(node.type, node.attrs);
  if (typeof node.text === 'string') sanitized.text = node.text;
  if (marks) sanitized.marks = marks;
  if (content) sanitized.content = content;
  return sanitized;
}

/** A ProseMirror mark rendered to JSON — safe to hand to `schema.nodeFromJSON`. */
export interface SanitizedMark {
  readonly type: string;
  readonly attrs?: Record<string, unknown>;
}

/** A ProseMirror node rendered to JSON — safe to hand to `schema.nodeFromJSON`. */
export interface SanitizedNode {
  readonly type: string;
  readonly attrs?: Record<string, unknown>;
  readonly text?: string;
  readonly marks?: readonly SanitizedMark[];
  readonly content?: readonly SanitizedNode[];
}

/** Raised when agent-supplied inline-edit content violates the allowlist or link policy. */
export class InlineEditSanitizationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'InlineEditSanitizationError';
  }
}

/**
 * Validates and normalizes an agent-supplied inline-edit replacement node before it
 * reaches `schema.nodeFromJSON`, which validates node/mark types but never attribute
 * values. It rejects unknown node/mark types; forces safe rel/target on every link so
 * the rendered anchor cannot spoof or reverse-tabnab; and enforces the shared
 * content-link policy on every URL that reaches a sink — link mark hrefs and the
 * URL-bearing node attributes (image/youtube src). Other node attributes pass through
 * (ProseMirror drops unknown keys) and are further constrained at render by CSP.
 *
 * @throws {InlineEditSanitizationError} when the payload violates the allowlist or link policy.
 */
export function sanitizeInlineEditReplacement(replacement: unknown): SanitizedNode {
  return sanitizeNode(replacement);
}
